import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { FaCircle } from "react-icons/fa";
import { useNavigate } from "react-router";
import { CommonContext } from "../../App";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../Components/CommonComponent/loadingSpiner/LoadingSpinner";
import DateFilter from "../../Components/CommonComponent/Dropdown/DateFilter";
import api from "../../services/api";

export default function BalanceSheet() {

  const { t } = useTranslation();
  const [branchSheets, setBranchSheets] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});

  const navigate = useNavigate();

  const { branchId, dateRange, setDateRange } = useContext(CommonContext);
  const [startDate, setStartDate] = useState(dateRange.endDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);
  const storedLang = JSON.parse(localStorage.getItem("second_language"));
  const language = storedLang?.language || "en";
  useEffect(() => {
    setStartDate(dateRange.endDate);
    setEndDate(dateRange.endDate);
  }, [dateRange.endDate]);

  const [loading, setLoading] = useState(false);
  const formatAmount = (value) => {
    const num = Number(value);
    return Number.isInteger(num) ? num : num.toFixed(1);
  };


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const res = await api.get(`ledgers/get-balance-sheet-summary`, {
          params: { startDate, endDate, branchId },
        });

        if (res.data.success) {
          const branchSheetsTemp = res.data.data.map((branchBlock) => {
            const { branchId, branchName, branchData } = branchBlock;

            const tableRows = [];
            const tableplRows = [];

            branchData.forEach((ledger) => {


              const openingRaw = Number(ledger.openingBalance || 0);
              const openingSigned =
                ledger.openingBalanceType === "credit"
                  ? -openingRaw
                  : openingRaw;


              const received = Number(ledger.totalReceived || 0);
              const paid = Number(ledger.totalPaid || 0);
              const movement = received - paid;


              const closing = openingSigned + movement;

              const debit = closing >= 0 ? closing : 0;
              const credit = closing < 0 ? Math.abs(closing) : 0;

              // Base mapping row
              const baseRow = {
                id: ledger.id,
                account: ledger.name,
                accountInHindi: ledger.nameInHindi,
                groupName: ledger.groupName || ledger.name,
                groupNameInHindi: ledger.groupNameInHindi,
                subGroupName: ledger.subGroupName,
                subGroupNameInHindi: ledger.subGroupNameInHindi,
                type: ledger.groupType,

                // 🔥 Added fields
                openingBalance: openingRaw,
                openingBalanceType: ledger.openingBalanceType,
                movement, // received - paid
                debit,
                credit,
                closingBalance: closing,

                branchId,
                branchName,
              };
              // console.log("base row",baseRow)

              if (["Assets", "Liability", "Equity"].includes(ledger.groupType)) {
                tableRows.push(baseRow);
              }

              if (["Income", "Expense"].includes(ledger.groupType)) {
                tableplRows.push(baseRow);
              }
            });

            return {
              branchId,
              branchName,
              trialBalanceRows: tableRows,
              plBalanceRows: tableplRows,
            };
          });

          setBranchSheets(branchSheetsTemp);
        }
      } catch (error) {
        console.error("Error fetching trial balance:", error);
      } finally {
        setLoading(false);
      }
    };

    if (startDate && endDate && branchId) fetchData();

  }, [startDate, endDate, branchId]);


  const structureRows = (rows) => {
    const data = {};
  // console.log("row", rows)
    rows.forEach((r) => {
      const group = r.groupName || r.account;
       
      if (!data[group]) {
        data[group] = {
          groupName: group,
          groupNameInHindi: r.groupNameInHindi || r.accountInHindi,
          subGroupName:r.subGroupName,
          subGroupNameInHindi:r.subGroupNameInHindi,
          groupType: r.type,
          subGroups: {},
          totalDebit: 0,
          totalCredit: 0,
        };
      }

      data[group].totalDebit += r.debit;
      data[group].totalCredit += r.credit;

      if (r.subGroupName) {
        if (!data[group].subGroups[r.subGroupName]) {
          data[group].subGroups[r.subGroupName] = {
            subGroupName: r.subGroupName,
            subGroupNameInHindi: r.subGroupNameInHindi,
            debit: 0,
            credit: 0,
            accounts: [],
          };
        }
        data[group].subGroups[r.subGroupName].debit += r.debit;
        data[group].subGroups[r.subGroupName].credit += r.credit;
        data[group].subGroups[r.subGroupName].accounts.push(r);
      }
    });

    // Remove groups with zero closing balance
    Object.keys(data).forEach((group) => {
      const g = data[group];
      const groupClosing = Math.abs(g.totalDebit - g.totalCredit);
      if (groupClosing === 0) {
        delete data[group];
      } else {
        // Remove subgroups with zero closing balance
        Object.keys(g.subGroups).forEach((sub) => {
          const s = g.subGroups[sub];
          const subClosing = Math.abs(s.debit - s.credit);
          if (subClosing === 0) {
            delete g.subGroups[sub];
          }
        });
      }
    });

    return data;
  };


  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const totalDebit = (rows) =>
    Object.values(rows).reduce((sum, g) => sum + g.totalDebit, 0);

  const totalCredit = (rows) =>
    Object.values(rows).reduce((sum, g) => sum + g.totalCredit, 0);

  const renderTable = (structuredData) => (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b  text-theme-headerBg  border-theme-headerBg">
          <th className="px-1 font-semibold text-left">{t("ledger/account")}</th>
          <th className="px-1 font-semibold text-right">{t("closingBalance")} (₹)</th>
        </tr>
      </thead>

      <tbody>
  {Object.keys(structuredData).length === 0 ? (
    <tr>
      <td
        colSpan={2}
        className="text-center py-3 text-gray-400 font-semibold"
      >
        {t("noDataAvailable")}
      </td>
    </tr>
  ) : (
    Object.entries(structuredData).map(([groupName, gData]) => {
      const hasSubGroups = Object.keys(gData.subGroups).length > 0;
      const isExpanded = expandedGroups[groupName];

      return (
        <React.Fragment key={groupName}>
          <tr
            className="border-b bg-theme-tableBg hover:bg-theme-tableHoverColor cursor-pointer"
            onClick={() => hasSubGroups && toggleGroup(groupName)}
          >
            <td className="py-[2px] px-1">
              {groupName}{" "}
              {language !== "en" &&
                gData.groupNameInHindi &&
                gData.groupNameInHindi}

              {hasSubGroups && (
                <span
                  className={`text-[9.5px] ml-2 transition-transform ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                >
                  {isExpanded ? "▲" : "▼"}
                </span>
              )}
            </td>

            <td className="px-1 text-right">
              {gData.totalDebit > gData.totalCredit
                ? `${formatAmount(
                    gData.totalDebit - gData.totalCredit
                  )} Dr`
                : `${formatAmount(
                    gData.totalCredit - gData.totalDebit
                  )} Cr`}
            </td>
          </tr>

          {isExpanded &&
            Object.entries(gData.subGroups).map(([subName, vals]) => (
              <tr
                key={subName}
                className="border-b bg-theme-tableBg hover:bg-theme-tableHoverColor text-sm"
              >
                <td
                  className="px-5 py-[2px] cursor-pointer flex items-center gap-2"
                  onClick={subName === 'difference in opening balance' ? undefined: () =>
                    navigate(`/account-books/ledgers-table`, {
                      state: {
                        ledger: gData,
                        btnHeading: "Balance Sheet",
                        btnlink: "/reports/balance-sheet",
                      },
                    })
                  }
                >
                  <FaCircle className="text-[7px]" />
                  {subName}{" "}
                  {language !== "en" &&
                    vals.subGroupNameInHindi &&
                    vals.subGroupNameInHindi}
                </td>

                <td className="px-1 text-right">
                  {vals.debit > vals.credit
                    ? `${vals.debit - vals.credit} Dr`
                    : `${vals.credit - vals.debit} Cr`}
                </td>
              </tr>
            ))}
        </React.Fragment>
      );
    })
  )}
</tbody>

    </table>
  );



  return (
    <div className=" mt-2 bg-white shadow-sm rounded  p-2">
      <div className="flex justify-between  bg-theme-headerBg text-theme-color rounded-1   px-2 py-1 mb-1">
        <div className=" font-medium  mt-[4px]">{t("balanceSheet")}</div>

        <div className="flex items-center gap-2 text-theme-color">
          <div className=" font-medium text-sm  ">As at :</div>
          <div className="text-theme-headerBg bg-white p-[2px] rounded-1">
            <DateFilter
             storageKey="balanceSheet"
              customMode="single"
              availableFilters={["today", "custom"]}
              onChange={(r) =>
                setDateRange({
                  startDate: '',
                  endDate: r.endDate,
                })
              }
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10">
          <LoadingSpinner size={70} color="#000000" />
        </div>
      ) : (
        branchSheets.map((branch) => {
          const trial = branch.trialBalanceRows;
          const pl = branch.plBalanceRows;

          const assets = trial.filter((r) => r.type === "Assets");
          const liabilities = trial.filter((r) => r.type === "Liability");
          const equity = trial.filter((r) => r.type === "Equity");

          const income = pl.filter((r) => r.type === "Income");
          const expense = pl.filter((r) => r.type === "Expense");

          // Net profit/loss
          const totalIncome =
            income.reduce((s, r) => s + r.credit, 0) -
            income.reduce((s, r) => s + r.debit, 0);

          const totalExpense =
            expense.reduce((s, r) => s + r.debit, 0) -
            expense.reduce((s, r) => s + r.credit, 0);

          const netProfit = totalIncome - totalExpense;

          const equityWithProfit = [...equity];
          if (netProfit !== 0) {
            equityWithProfit.push({
              id: "netProfit",
              account: netProfit > 0 ? "Net Profit" : "Net Loss",
              groupName: netProfit > 0 ? "Net Profit" : "Net Loss",
              groupNameInHindi: netProfit > 0 ? " लाभ" : " हानि",
              debit: netProfit < 0 ? Math.abs(netProfit) : 0,
              credit: netProfit > 0 ? netProfit : 0,
              type: "Equity",
            });
          }

          const structuredAssets = structureRows(assets);
          const structuredLiabilities = structureRows([
            ...liabilities,
            ...equityWithProfit,
          ]);

          const totalA =
            Math.abs(
              totalDebit(structuredAssets) - totalCredit(structuredAssets)
            );

          const totalL =
            Math.abs(
              totalDebit(structuredLiabilities) - totalCredit(structuredLiabilities)
            );
            // console.log("check total", totalA, totalL)

          return (
            <div key={branch.branchId} className="mb-2">
              <div className=" font-serif font-medium mb-2  px-1">
                <div className="flex justify-center text-theme-headerBg">
                  <div className="border-b  border-theme-sidebarBg capitalize">
                    {branch.branchName}
                  </div>
                  {/* <div className=" font-medium mt-[5px]">{t("balanceSheet")}</div> */}
                  {/* <div className="flex items-center gap-2">
          <div className=" font-medium text-sm text-theme-headerBg ">As at :</div>
          <DateFilter
            customMode="single"
            availableFilters={["today", "custom"]}
            onChange={(r) =>
              setDateRange({
                startDate: r.startDate,
                endDate: r.startDate,
              })
            }
          />
        </div> */}
                </div>

              </div>

              {trial.length === 0 && pl.length === 0 ? (
                <div className="text-center text-red-500 py-3 font-semibold">
                  {t("noDataAvailable")}
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <div className="w-1/2">
                      <div className="font-semibold text-sm  mb-1 px-2 py-[2px] bg-theme-headerBg  text-theme-color">
                        {t("liability&Equity")}
                      </div>
                      {renderTable(structuredLiabilities)}
                    </div>

                    <div className="w-1/2">
                      <div className="font-semibold text-sm  mb-1 px-2 py-[2px] bg-theme-headerBg text-theme-color">
                        {t("assets")}
                      </div>
                      {renderTable(structuredAssets)}
                    </div>
                  </div>

                  <div
                    className={`flex w-full font-semibold mt-3 text-sm  ${totalA.toFixed(2) === totalL.toFixed(2) ? "text-green-700" : "text-red-600"
                      }`}
                  >
                    <div className="w-1/2 px-1">
                      <div className="border-t border-theme-headerBg flex justify-between pt-1">
                        <div>{t("total")}</div>
                        <div className="pe-2">{totalL.toLocaleString("en-IN")}</div>
                      </div>
                    </div>

                    <div className="w-1/2 ps-3">
                      <div className="border-t border-theme-headerBg flex justify-between pt-1">
                        <div>{t("total")}</div>
                        <div className="pe-2">{totalA.toLocaleString("en-IN")}</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}